import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Plus, Trash2, Save, Pencil, X, Upload as UploadIcon } from "lucide-react";
import { ASSETS } from "@/lib/assets";

const CATEGORY_OPTIONS = ["Empresarios", "Médicos", "Emprendedores", "Ejecutivos", "Creadores", "Profesionales", "Expertos"];
const PLATFORM_OPTIONS = ["Instagram Reel", "TikTok", "YouTube Short"];

export function EpisodesCMS() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const r = await api.get("/admin/episodes");
    setItems(r.data);
  };
  useEffect(() => { load(); }, []);

  const blank = () => ({
    guest_name: "", profession: "", title: "", description: "",
    thumbnail_url: "", youtube_url: "", spotify_url: "", published_at: "",
    order: (items.length + 1) * 10, is_published: false,
  });

  const save = async () => {
    setSaving(true);
    try {
      if (editing.id) {
        await api.put(`/admin/episodes/${editing.id}`, editing);
      } else {
        await api.post("/admin/episodes", editing);
      }
      toast.success("Episodio guardado");
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!confirm("¿Eliminar este episodio?")) return;
    await api.delete(`/admin/episodes/${id}`);
    toast.success("Eliminado");
    load();
  };

  return (
    <div data-testid="cms-episodes">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-2xl text-white">Episodios</h2>
        <button data-testid="cms-episode-new" onClick={() => setEditing(blank())} className="mp-btn-primary !py-2 !px-4 !text-[11px]"><Plus size={14}/> Nuevo</button>
      </div>

      <div className="border border-white/10">
        <div className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-white/10 text-[10px] tracking-[0.24em] uppercase text-mp-mute font-display">
          <div className="col-span-1">#</div>
          <div className="col-span-4">Episodio</div>
          <div className="col-span-3">Invitado</div>
          <div className="col-span-2">Estado</div>
          <div className="col-span-2 text-right">Acciones</div>
        </div>
        {items.length === 0 && <div className="p-8 text-mp-mute text-sm">Sin episodios aún.</div>}
        {items.map((ep) => (
          <div key={ep.id} data-testid={`cms-ep-row-${ep.id}`} className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-white/5 items-center">
            <div className="col-span-1 text-mp-mute text-sm">{ep.order}</div>
            <div className="col-span-4">
              <div className="text-white font-display">{ep.title || "(sin título)"}</div>
              <div className="text-mp-mute text-xs">{ep.profession}</div>
            </div>
            <div className="col-span-3 text-white/80 text-sm">{ep.guest_name}</div>
            <div className="col-span-2">
              <span className={`inline-flex px-2 py-1 text-[10px] tracking-[0.2em] uppercase font-display ${ep.is_published ? "bg-mp-orange/20 text-mp-orange" : "border border-white/15 text-white/60"}`}>
                {ep.is_published ? "Publicado" : "Próximamente"}
              </span>
            </div>
            <div className="col-span-2 flex items-center gap-2 justify-end">
              <button data-testid={`cms-ep-edit-${ep.id}`} onClick={() => setEditing(ep)} className="text-white/60 hover:text-mp-orange"><Pencil size={14} /></button>
              <button data-testid={`cms-ep-del-${ep.id}`} onClick={() => del(ep.id)} className="text-white/60 hover:text-red-400"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <Drawer title={editing.id ? "Editar episodio" : "Nuevo episodio"} onClose={() => setEditing(null)}>
          <Grid2>
            <F label="Invitado *"><input className="mp-input" value={editing.guest_name} onChange={(e) => setEditing({...editing, guest_name: e.target.value})}/></F>
            <F label="Profesión / Expertise *"><input className="mp-input" value={editing.profession} onChange={(e) => setEditing({...editing, profession: e.target.value})}/></F>
            <F label="Título del episodio *" span={2}><input className="mp-input" value={editing.title} onChange={(e) => setEditing({...editing, title: e.target.value})}/></F>
            <F label="Descripción" span={2}><textarea className="mp-input" rows={3} value={editing.description} onChange={(e) => setEditing({...editing, description: e.target.value})}/></F>
            <F label="Thumbnail URL"><input className="mp-input" value={editing.thumbnail_url} onChange={(e) => setEditing({...editing, thumbnail_url: e.target.value})}/></F>
            <F label="Fecha (YYYY-MM-DD)"><input className="mp-input" value={editing.published_at || ""} onChange={(e) => setEditing({...editing, published_at: e.target.value})}/></F>
            <F label="YouTube URL"><input className="mp-input" placeholder="https://youtube.com/…" value={editing.youtube_url} onChange={(e) => setEditing({...editing, youtube_url: e.target.value})}/></F>
            <F label="Spotify URL"><input className="mp-input" placeholder="https://open.spotify.com/…" value={editing.spotify_url} onChange={(e) => setEditing({...editing, spotify_url: e.target.value})}/></F>
            <F label="Orden"><input type="number" className="mp-input" value={editing.order} onChange={(e) => setEditing({...editing, order: parseInt(e.target.value || "0")})}/></F>
            <F label="Estado">
              <label className="flex items-center gap-3 pt-4 cursor-pointer">
                <input type="checkbox" checked={editing.is_published} onChange={(e) => setEditing({...editing, is_published: e.target.checked})} className="accent-mp-orange w-4 h-4"/>
                <span className="text-white/80 text-sm">Publicado (visible en el sitio)</span>
              </label>
            </F>
          </Grid2>
          <DrawerActions saving={saving} onSave={save} onCancel={() => setEditing(null)} testid="cms-ep-save"/>
        </Drawer>
      )}
    </div>
  );
}

export function GuestsCMS() {
  const [items, setItems] = useState([]);
  const [episodes, setEpisodes] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [g, e] = await Promise.all([api.get("/admin/guests"), api.get("/admin/episodes")]);
    setItems(g.data); setEpisodes(e.data);
  };
  useEffect(() => { load(); }, []);

  const blank = () => ({ name: "", profession: "", category: "Empresarios", company: "", expertise: "", photo_url: "", episode_id: "", order: (items.length + 1) * 10, is_featured: true });

  const save = async () => {
    setSaving(true);
    try {
      if (editing.id) await api.put(`/admin/guests/${editing.id}`, editing);
      else await api.post("/admin/guests", editing);
      toast.success("Invitado guardado"); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!confirm("¿Eliminar este invitado público?")) return;
    await api.delete(`/admin/guests/${id}`);
    toast.success("Eliminado"); load();
  };

  return (
    <div data-testid="cms-guests">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-2xl text-white">Invitados Públicos</h2>
        <button data-testid="cms-guest-new" onClick={() => setEditing(blank())} className="mp-btn-primary !py-2 !px-4 !text-[11px]"><Plus size={14}/> Nuevo</button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {items.map((g) => (
          <div key={g.id} data-testid={`cms-guest-card-${g.id}`} className="relative aspect-[3/4] border border-white/10 overflow-hidden group">
            {g.photo_url ? <img src={g.photo_url} alt={g.name} className="h-full w-full object-cover"/> : <div className="h-full w-full bg-mp-charcoal"/>}
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent"/>
            <div className="absolute bottom-0 inset-x-0 p-4">
              <div className="text-mp-orange text-[10px] tracking-[0.24em] uppercase font-display">{g.category}</div>
              <div className="mt-1 font-display text-white text-sm leading-tight">{g.name}</div>
              <div className="text-white/70 text-xs">{g.profession}</div>
            </div>
            <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button onClick={() => setEditing(g)} className="bg-black/70 p-1.5 text-white"><Pencil size={12}/></button>
              <button onClick={() => del(g.id)} className="bg-black/70 p-1.5 text-white hover:text-red-400"><Trash2 size={12}/></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="col-span-full p-8 text-mp-mute text-sm border border-white/10">Sin invitados públicos.</div>}
      </div>

      {editing && (
        <Drawer title={editing.id ? "Editar invitado" : "Nuevo invitado"} onClose={() => setEditing(null)}>
          <Grid2>
            <F label="Nombre *"><input className="mp-input" value={editing.name} onChange={(e) => setEditing({...editing, name: e.target.value})}/></F>
            <F label="Profesión *"><input className="mp-input" value={editing.profession} onChange={(e) => setEditing({...editing, profession: e.target.value})}/></F>
            <F label="Categoría"><select className="mp-input bg-mp-black" value={editing.category} onChange={(e) => setEditing({...editing, category: e.target.value})}>{CATEGORY_OPTIONS.map((c) => <option key={c}>{c}</option>)}</select></F>
            <F label="Empresa"><input className="mp-input" value={editing.company} onChange={(e) => setEditing({...editing, company: e.target.value})}/></F>
            <F label="Expertise" span={2}><input className="mp-input" value={editing.expertise} onChange={(e) => setEditing({...editing, expertise: e.target.value})}/></F>
            <F label="Foto URL" span={2}><input className="mp-input" placeholder="https://…" value={editing.photo_url} onChange={(e) => setEditing({...editing, photo_url: e.target.value})}/></F>
            <F label="Episodio vinculado">
              <select className="mp-input bg-mp-black" value={editing.episode_id || ""} onChange={(e) => setEditing({...editing, episode_id: e.target.value})}>
                <option value="">Sin vincular</option>
                {episodes.map((e) => <option key={e.id} value={e.id}>{e.title || e.guest_name}</option>)}
              </select>
            </F>
            <F label="Orden"><input type="number" className="mp-input" value={editing.order} onChange={(e) => setEditing({...editing, order: parseInt(e.target.value || "0")})}/></F>
            <F label="Estado" span={2}>
              <label className="flex items-center gap-3 pt-4 cursor-pointer">
                <input type="checkbox" checked={editing.is_featured} onChange={(e) => setEditing({...editing, is_featured: e.target.checked})} className="accent-mp-orange w-4 h-4"/>
                <span className="text-white/80 text-sm">Visible en el sitio</span>
              </label>
            </F>
          </Grid2>
          <DrawerActions saving={saving} onSave={save} onCancel={() => setEditing(null)} testid="cms-guest-save"/>
        </Drawer>
      )}
    </div>
  );
}

export function SettingsCMS() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const r = await api.get("/settings");
    setS(r.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", s);
      toast.success("Guardado");
    } catch (e) { toast.error("Error"); }
    finally { setSaving(false); }
  };

  const [importUrl, setImportUrl] = useState("");
  const [importing, setImporting] = useState(false);
  const addMoment = () => setS({ ...s, moments: [...(s.moments || []), { title: "", url: "", platform: "Instagram Reel", thumbnail: "" }] });
  const importMoment = async () => {
    const id = importUrl.match(/\/video\/(\d+)/)?.[1];
    if (id && (s.moments || []).some((m) => (m.url || "").includes(id))) { toast.error("Ese TikTok ya está en Momentos"); return; }
    setImporting(true);
    try {
      const r = await api.post("/admin/moments/import", { url: importUrl });
      setS({ ...s, moments: [r.data, ...(s.moments || [])] });
      setImportUrl("");
      toast.success("TikTok agregado al inicio. Pulsa «Guardar cambios» para publicarlo.");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo importar"); }
    finally { setImporting(false); }
  };
  const upMoment = (i, k, v) => { const m = [...s.moments]; m[i] = { ...m[i], [k]: v }; setS({ ...s, moments: m }); };
  const rmMoment = (i) => setS({ ...s, moments: s.moments.filter((_, j) => j !== i) });

  if (!s) return <div className="text-mp-mute">Cargando…</div>;

  return (
    <div data-testid="cms-settings" className="space-y-10">
      <section>
        <h2 className="font-display text-2xl text-white mb-6">Redes &amp; Contacto</h2>
        <Grid2>
          <F label="Instagram URL"><input className="mp-input" value={s.instagram} onChange={(e) => setS({...s, instagram: e.target.value})}/></F>
          <F label="YouTube URL"><input className="mp-input" value={s.youtube} onChange={(e) => setS({...s, youtube: e.target.value})}/></F>
          <F label="TikTok URL"><input className="mp-input" value={s.tiktok} onChange={(e) => setS({...s, tiktok: e.target.value})}/></F>
          <F label="Spotify Show URL (vacío = próximamente)"><input className="mp-input" value={s.spotify} onChange={(e) => setS({...s, spotify: e.target.value})}/></F>
          <F label="Apple Podcasts URL (vacío = próximamente)"><input className="mp-input" value={s.apple_podcasts} onChange={(e) => setS({...s, apple_podcasts: e.target.value})}/></F>
          <F label="Email"><input className="mp-input" value={s.email} onChange={(e) => setS({...s, email: e.target.value})}/></F>
          <F label="Teléfono"><input className="mp-input" value={s.phone} onChange={(e) => setS({...s, phone: e.target.value})}/></F>
        </Grid2>
      </section>

      <section>
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-2xl text-white">Momentos Modo Pro</h2>
          <button data-testid="cms-moment-add" onClick={addMoment} className="mp-btn-ghost !py-2 !px-4"><Plus size={14}/> Agregar</button>
        </div>
        <div className="mb-6 border border-mp-orange/30 p-4 flex flex-col md:flex-row gap-3 md:items-end">
          <div className="flex-1">
            <div className="mp-label">Agregar TikTok por enlace (portada y título automáticos)</div>
            <input data-testid="cms-moment-import-url" className="mp-input" placeholder="https://www.tiktok.com/@modopropodcast/video/…" value={importUrl} onChange={(e) => setImportUrl(e.target.value)} />
          </div>
          <button data-testid="cms-moment-import-btn" disabled={importing || !importUrl} onClick={importMoment} className="mp-btn-primary !py-2 !px-4 !text-[11px]">{importing ? "Importando…" : "Importar"}</button>
        </div>
        <div className="space-y-4">
          {(s.moments || []).map((m, i) => (
            <div key={i} data-testid={`cms-moment-${i}`} className="border border-white/10 p-4 grid grid-cols-12 gap-3 items-start">
              <div className="col-span-3">
                <div className="mp-label">Plataforma</div>
                <select className="mp-input bg-mp-black" value={m.platform} onChange={(e) => upMoment(i, "platform", e.target.value)}>
                  {PLATFORM_OPTIONS.map((p) => <option key={p}>{p}</option>)}
                </select>
              </div>
              <div className="col-span-4">
                <div className="mp-label">Título (opcional)</div>
                <input className="mp-input" value={m.title || ""} onChange={(e) => upMoment(i, "title", e.target.value)}/>
              </div>
              <div className="col-span-4">
                <div className="mp-label">URL</div>
                <input className="mp-input" placeholder="https://…" value={m.url || ""} onChange={(e) => upMoment(i, "url", e.target.value)}/>
              </div>
              <div className="col-span-1 flex justify-end pt-8">
                <button onClick={() => rmMoment(i)} className="text-white/60 hover:text-red-400"><Trash2 size={14}/></button>
              </div>
              <div className="col-span-12">
                <div className="mp-label">Miniatura URL (opcional)</div>
                <input className="mp-input" placeholder="https://…" value={m.thumbnail || ""} onChange={(e) => upMoment(i, "thumbnail", e.target.value)}/>
              </div>
            </div>
          ))}
          {(!s.moments || s.moments.length === 0) && <div className="p-6 border border-white/10 text-mp-mute text-sm">Sin momentos. Agrega Reels, TikToks o Shorts oficiales.</div>}
        </div>
      </section>

      <div className="border-t border-white/10 pt-6 flex justify-end">
        <button data-testid="cms-settings-save" disabled={saving} onClick={save} className="mp-btn-primary"><Save size={14}/> {saving ? "Guardando…" : "Guardar cambios"}</button>
      </div>
    </div>
  );
}

// --- Reusable primitives ---
export function Grid2({ children }) { return <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">{children}</div>; }

export function F({ label, span, children }) {
  return (
    <div className={span === 2 ? "md:col-span-2" : ""}>
      <div className="mp-label">{label}</div>
      {children}
    </div>
  );
}

export function Drawer({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex" onClick={onClose}>
      <div className="ml-auto w-full max-w-[900px] h-full bg-mp-black border-l border-white/10 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-10 bg-mp-black/95 backdrop-blur border-b border-white/10 px-8 py-5 flex items-center justify-between">
          <h2 className="font-display text-xl text-white">{title}</h2>
          <button data-testid="drawer-close" onClick={onClose} className="text-white/70 hover:text-white"><X size={18}/></button>
        </div>
        <div className="p-8">{children}</div>
      </div>
    </div>
  );
}

export function DrawerActions({ saving, onSave, onCancel, testid }) {
  return (
    <div className="mt-10 border-t border-white/10 pt-6 flex justify-end gap-3">
      <button onClick={onCancel} className="mp-btn-ghost">Cancelar</button>
      <button data-testid={testid} disabled={saving} onClick={onSave} className="mp-btn-primary"><Save size={14}/> {saving ? "Guardando…" : "Guardar"}</button>
    </div>
  );
}
